import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import DOMPurify from 'dompurify';
import {
  ArrowLeft,
  Share2,
  Calendar,
  User,
  FileText,
  Download,
  Eye,
  Link as LinkIcon,
  Video,
  Music,
  ExternalLink
} from 'lucide-react';
import { PageTransition } from '../transitions/PageTransition';
import { getCategoryColor } from './newsData';
import { collection, query, where, limit, getDocs, orderBy, doc, updateDoc, increment, onSnapshot, documentId } from 'firebase/firestore';
import { db } from '../../firebase';
import { useUserAccess } from '../../context/UserAccessContext';

interface PortalGuruDetailBeritaProps {
  newsItem?: any;
  onBack?: () => void;
}

export const PortalGuruDetailBerita: React.FC<PortalGuruDetailBeritaProps> = ({ newsItem, onBack }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = (newsItem || location.state) as {
    title?: string;
    date?: string;
    category?: string;
    image?: string;
    author?: { email: string; name: string } | string;
    content?: string;
    attachments?: { name: string; type: string; data: string; url: string }[];
    externalLinks?: string[];
    relatedNews?: string[]; // IDs of manually selected related news
    id?: string; // We need ID for view tracking
  } | null;

  const [relatedNews, setRelatedNews] = useState<any[]>([]);
  const [viewCount, setViewCount] = useState<number>(0);
  const { userPermissions } = useUserAccess();

  // Scroll to top when location changes (i.e. when clicking related news)
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location, newsItem]);

  // Handle case where state is missing (e.g. direct link access - tough without ID in URL, but assuming flow from list)
  if (!state) {
      return (
          <div className="min-h-screen flex items-center justify-center">
              <p>Berita tidak ditemukan. Silakan kembali ke daftar berita.</p>
              <button onClick={() => onBack ? onBack() : navigate('/portal-guru/berita')}>Kembali</button>
          </div>
      );
  }

  const displayData = state;

  // Increment view count
  useEffect(() => {
    if (displayData.id) {
        const incrementViews = async () => {
            // Check session storage to avoid double counting per session if desired,
            // but for now simple increment per load
            const viewedKey = `viewed_news_${displayData.id}`;
            if (!sessionStorage.getItem(viewedKey)) {
                const docRef = doc(db, 'berita', displayData.id);
                try {
                    await updateDoc(docRef, {
                        views: increment(1)
                    });
                    sessionStorage.setItem(viewedKey, 'true');
                } catch (e) {
                    console.error("Error incrementing views", e);
                }
            }
        };
        incrementViews();

        // Realtime listener for views
        const unsub = onSnapshot(doc(db, 'berita', displayData.id), (doc) => {
            if (doc.exists()) {
                setViewCount(doc.data().views || 0);
            }
        });
        return () => unsub();
    }
  }, [displayData.id]);

  // Fetch related news (Manual or Automatic)
  useEffect(() => {
    const fetchRelated = async () => {
      try {
        // 1. Try Manual Selection
        if (displayData.relatedNews && displayData.relatedNews.length > 0) {
            const q = query(
                collection(db, 'berita'),
                where(documentId(), 'in', displayData.relatedNews)
            );
            const snapshot = await getDocs(q);
            const items = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setRelatedNews(items);
            return;
        }

        // 2. Fallback: Automatic (Latest published except current)
        const q = query(
          collection(db, 'berita'),
          where('status', '==', 'published'),
          orderBy('createdAt', 'desc'),
          limit(4) // Fetch 4, then filter client side to remove current
        );

        const snapshot = await getDocs(q);
        const items = snapshot.docs
            .map(doc => ({ id: doc.id, ...doc.data() }))
            .filter((item: any) => item.title !== displayData.title) // Filter out current
            .slice(0, 3); // Take top 3

        setRelatedNews(items);
      } catch (error) {
        console.error("Error fetching related news:", error);
      }
    };

    fetchRelated();
  }, [displayData.title, displayData.relatedNews]);

  const handleRelatedClick = (item: any) => {
    navigate('/portal-guru/berita/detail', {
      state: {
        ...item,
        date: item.createdAt ? new Intl.DateTimeFormat('id-ID', { day: 'numeric', month: 'short', year: 'numeric' }).format(item.createdAt.toDate()) : '',
        author: "Ustadz Admin" // Preserve author or set default
      }
    });
  };

  const handleDownload = (file: { name: string; url: string }) => {
    const link = document.createElement("a");
    link.href = file.url;
    link.download = file.name;
    link.target = "_blank"; // Important for Firebase Storage URLs
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Helper to resume audio playback
  const handleAudioTimeUpdate = (e: React.SyntheticEvent<HTMLAudioElement>, fileUrl: string) => {
      const audio = e.currentTarget;
      localStorage.setItem(`audio_time_${fileUrl}`, audio.currentTime.toString());
  };

  const handleAudioLoadedMetadata = (e: React.SyntheticEvent<HTMLAudioElement>, fileUrl: string) => {
      const audio = e.currentTarget;
      const savedTime = localStorage.getItem(`audio_time_${fileUrl}`);
      if (savedTime) {
          audio.currentTime = parseFloat(savedTime);
      }
  };

  // Helper to embed YouTube
  const getYoutubeEmbedUrl = (url: string) => {
      const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
      const match = url.match(regExp);
      return (match && match[2].length === 11) ? `https://www.youtube.com/embed/${match[2]}` : null;
  };

  // Helper to detect Google Drive Video
  const isGoogleDriveVideo = (url: string) => {
      return url.includes('drive.google.com') && (url.includes('/file/d/') || url.includes('id='));
  };

  // Convert Drive View URL to Preview URL for embedding if possible, or just return null to fallback to link
  const getDriveEmbedUrl = (url: string) => {
      if (!isGoogleDriveVideo(url)) return null;
      // Try to convert view/edit to preview
      return url.replace('/view', '/preview').replace('/edit', '/preview');
  };

  return (
    <div className="min-h-screen bg-[#f6f8f6] text-[#111813] font-sans antialiased pb-28">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-100 px-4 py-3 flex items-center justify-between transition-colors">
        <button
          className="flex items-center justify-center size-10 rounded-full hover:bg-gray-100 transition-colors text-[#111813]"
          onClick={() => onBack ? onBack() : navigate(-1)}
        >
          <ArrowLeft size={24} />
        </button>
        <button className="flex items-center justify-center size-10 rounded-full hover:bg-gray-100 transition-colors text-[#111813]">
          <Share2 size={24} />
        </button>
      </div>

      <PageTransition>
        <div className="w-full max-w-2xl mx-auto">
          {/* Hero Image */}
          {displayData.image && (
            <div className="w-full aspect-video bg-gray-200 relative">
                <div
                className="absolute inset-0 bg-center bg-cover bg-no-repeat"
                style={{ backgroundImage: `url("${displayData.image}")` }}
                ></div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-60"></div>
            </div>
          )}

          {/* Content Container */}
          <div className={`px-5 ${displayData.image ? '-mt-6' : 'mt-6'} relative z-10`}>
            <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
              {/* Meta Info */}
              <div className="flex items-center justify-between mb-3">
                <span className={`text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider ${getCategoryColor(displayData.category || 'akademik')}`}>
                  {displayData.category || 'Umum'}
                </span>
                <div className="flex items-center gap-4 text-[#63886f]">
                  <div className="flex items-center gap-1">
                    <Calendar size={16} />
                    <span className="text-xs">{displayData.date}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Eye size={16} />
                    <span className="text-xs">{viewCount}x dilihat</span>
                  </div>
                </div>
              </div>

              {/* Title */}
              <h1 className="text-xl md:text-2xl font-bold text-[#111813] leading-tight mb-4">
                {displayData.title}
              </h1>

              {/* Author */}
              <div className="flex items-center gap-3 pb-6 border-b border-gray-100 mb-6">
                <div className="size-8 rounded-full bg-[#19e65e]/20 flex items-center justify-center text-[#19e65e]">
                  <User size={18} />
                </div>
                <div className="flex flex-col">
                  <span className="text-xs text-[#63886f]">Penulis</span>
                  <span className="text-sm font-semibold text-[#111813]">
                    {(() => {
                        const author = displayData.author;
                        // Handle legacy string author or new object author
                        if (typeof author === 'string') return author || "Admin";

                        const email = author?.email;
                        const name = author?.name;

                        const isAuthorAdmin = userPermissions.find(p => p.email === email)?.role === 'super_admin';
                        if (isAuthorAdmin) {
                            return "Admin";
                        }

                        return name ? `Ustadz ${name}` : "Admin";
                    })()}
                  </span>
                </div>
              </div>

              {/* Article Content */}
              <div
                className="article-content text-[#63886f] text-sm md:text-base space-y-5 prose prose-sm max-w-none prose-headings:text-[#111813] prose-strong:text-[#111813] prose-a:text-[#19e65e]"
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(displayData.content || '') }}
              />

              {/* External Links (YouTube / Drive) */}
              {displayData.externalLinks && displayData.externalLinks.length > 0 && (
                  <div className="mt-8 pt-6 border-t border-gray-100">
                      <h4 className="text-sm font-bold text-[#111813] mb-3 flex items-center gap-2">
                          <Video size={18} />
                          Video / Link Eksternal
                      </h4>
                      <div className="grid gap-4">
                          {displayData.externalLinks.map((link, idx) => {
                              const ytEmbed = getYoutubeEmbedUrl(link);
                              const driveEmbed = getDriveEmbedUrl(link);

                              if (ytEmbed) {
                                  return (
                                      <div key={idx} className="rounded-xl overflow-hidden shadow-sm border border-gray-200 aspect-video">
                                          <iframe
                                              width="100%"
                                              height="100%"
                                              src={ytEmbed}
                                              title="YouTube video player"
                                              frameBorder="0"
                                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                              allowFullScreen
                                          ></iframe>
                                      </div>
                                  );
                              } else if (driveEmbed) {
                                   return (
                                      <div key={idx} className="rounded-xl overflow-hidden shadow-sm border border-gray-200 aspect-video">
                                          <iframe src={driveEmbed} width="100%" height="100%" allow="autoplay"></iframe>
                                      </div>
                                   );
                              } else {
                                  return (
                                      <a key={idx} href={link} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 bg-purple-50 p-3 rounded-xl border border-purple-100 hover:bg-purple-100 transition-colors">
                                          <div className="h-10 w-10 rounded-lg bg-white flex items-center justify-center shrink-0 text-purple-600 shadow-sm">
                                              <ExternalLink size={20} />
                                          </div>
                                          <div className="flex flex-col flex-1 min-w-0">
                                              <span className="text-sm font-medium text-[#111813] truncate">Buka Link Eksternal</span>
                                              <span className="text-xs text-gray-500 truncate">{link}</span>
                                          </div>
                                      </a>
                                  );
                              }
                          })}
                      </div>
                  </div>
              )}

              {/* Attachments */}
              {displayData.attachments && displayData.attachments.length > 0 && (
                <div className="mt-8 pt-6 border-t border-gray-100">
                    <h4 className="text-sm font-bold text-[#111813] mb-3 flex items-center gap-2">
                        <FileText size={18} />
                        Lampiran File
                    </h4>
                    <div className="grid gap-3">
                        {displayData.attachments.map((file, idx) => {
                            const isAudio = file.type.startsWith('audio/') || file.name.endsWith('.mp3');

                            if (isAudio) {
                                return (
                                    <div key={idx} className="bg-gray-50 p-3 rounded-xl border border-gray-200">
                                        <div className="flex items-center gap-3 mb-2">
                                            <div className="h-8 w-8 rounded-lg bg-indigo-100 flex items-center justify-center shrink-0 text-indigo-600">
                                                <Music size={18} />
                                            </div>
                                            <span className="text-sm font-medium text-[#111813] truncate">{file.name}</span>
                                        </div>
                                        <div className="w-full max-w-full overflow-hidden">
                                            <audio
                                                controls
                                                className="w-full h-10 block"
                                                style={{ maxWidth: '100%' }}
                                                src={file.url || file.data}
                                                onTimeUpdate={(e) => handleAudioTimeUpdate(e, file.url || file.name)}
                                                onLoadedMetadata={(e) => handleAudioLoadedMetadata(e, file.url || file.name)}
                                            >
                                                Browser Anda tidak mendukung elemen audio.
                                            </audio>
                                        </div>
                                    </div>
                                );
                            }

                            return (
                                <div key={idx} className="flex items-center justify-between bg-gray-50 p-3 rounded-xl border border-gray-200">
                                    <div className="flex items-center gap-3 overflow-hidden">
                                        <div className="h-10 w-10 rounded-lg bg-white flex items-center justify-center shrink-0 text-gray-500 shadow-sm">
                                            <FileText size={20} />
                                        </div>
                                        <span className="text-sm font-medium text-[#111813] truncate">{file.name}</span>
                                    </div>
                                    <button
                                        onClick={() => handleDownload(file)}
                                        className="p-2 rounded-full hover:bg-gray-200 text-[#19e65e] transition-colors"
                                        title="Download"
                                    >
                                        <Download size={20} />
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                </div>
              )}
            </div>
          </div>

          {/* Related News */}
          <div className="px-5 mt-8">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-[#111813]">Berita Terkait</h3>
            </div>
            <div className="flex flex-col gap-4">
              {relatedNews.length > 0 ? (
                relatedNews.map((item, index) => (
                  <div
                    key={index}
                    onClick={() => handleRelatedClick(item)}
                    className="bg-white rounded-xl p-3 shadow-sm border border-gray-100 flex gap-3 items-start active:scale-[0.99] transition-transform cursor-pointer"
                  >
                    <div
                      className="w-24 h-24 shrink-0 rounded-lg bg-center bg-cover bg-no-repeat bg-gray-200"
                      style={{ backgroundImage: item.image ? `url("${item.image}")` : 'none' }}
                    >
                         {!item.image && <div className="w-full h-full flex items-center justify-center text-gray-400"><Calendar size={24}/></div>}
                    </div>
                    <div className="flex flex-col justify-between h-24 py-0.5 w-full">
                      <div className="flex flex-col gap-1">
                        <span className={`text-[10px] font-bold uppercase tracking-wide w-fit px-2 py-0.5 rounded-full ${getCategoryColor(item.category || 'Umum')}`}>
                          {item.category || 'Umum'}
                        </span>
                        <h4 className="text-sm font-bold text-[#111813] leading-snug line-clamp-2">{item.title}</h4>
                      </div>
                      <span className="text-xs text-[#63886f]">
                        {item.createdAt ? new Intl.DateTimeFormat('id-ID', { day: 'numeric', month: 'short', year: 'numeric' }).format(item.createdAt.toDate()) : ''}
                      </span>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-sm text-gray-500 italic">Tidak ada berita terkait lainnya.</p>
              )}
            </div>
          </div>
          <div className="h-12"></div>
        </div>
      </PageTransition>

      {/* Floating Back Button (Bottom) */}
      <div className="fixed bottom-6 left-0 right-0 z-50 flex justify-center pointer-events-none">
        <button
          className="pointer-events-auto flex items-center gap-2 bg-[#111813] text-white px-6 py-3 rounded-full shadow-lg shadow-gray-200/50 border border-gray-100 hover:bg-black/90 active:scale-95 transition-all"
          onClick={() => onBack ? onBack() : navigate(-1)}
        >
          <ArrowLeft className="text-white" size={24} />
          <span className="font-medium text-sm">Kembali</span>
        </button>
      </div>
    </div>
  );
};
